import request from '../request';
import reservations from '../../services/gma/reservations';
import {types} from '../../utils/js/types';
import {get} from "../../utils/js/object";

import {GMI, STATE} from '../../constants';
import {isArrayNotEmpty} from "../../utils/js/array";

export const {CAR_CLASS} = STATE.UI.RESERVATIONS;

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearResCarClass() {
  return {type: GMI.SET_GMI_STATE, path: CAR_CLASS, value: {}};
}

/**
 * @private
 * @param {Object} car_class
 * @returns {Object} {type: string, path: [], value: object}
 */
function setResCarClass(car_class) {
  return {type: GMI.SET_GMI_STATE, path: CAR_CLASS, value: car_class};
}

/**
 * @private
 * @param {Object} code
 * @returns {Object} {type: string, path: [], value: object}
 */
function setCarClassCode(code) {
  return {type: GMI.SET_GMI_STATE, path: CAR_CLASS.concat(['code']), value: code};
}

/**
 * @private
 * @param {string} bundle_code
 * @returns {Object} {type: string, path: [], value: string}
 */
function setBundleCode(bundle_code) {
  return {type: GMI.SET_GMI_STATE, path: CAR_CLASS.concat(['bundle_code']), value: bundle_code};
}

/**
 * @private
 * @param {string} prepay_selected
 * @returns {Object} {type: string, path: [], value: string}
 */
function setPrePay(prepay_selected) {
  return {type: GMI.SET_GMI_STATE, path: CAR_CLASS.concat(['prepay_selected']), value: prepay_selected};
}

/**
 * @private
 * @param {Number} redemption_day_count
 * @returns {Object} {type: string, path: [], value: number}
 */
function setRedemptionDayCount(redemption_day_count) {
  return {type: GMI.SET_GMI_STATE, path: CAR_CLASS.concat(['redemption_day_count']), value: redemption_day_count};
}

export const carClassState = {
  /**
   * @name set
   * @desc A global setter for reservation.selectCarClass payload data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.reservations.carClassState
   * @example
   * store.dispatch(actions.reservations.carClassState.set(null, {code:'ECAR', name:'Economy'}));
   * store.dispatch(actions.reservations.carClassState.set('code', {code:'ECAR'}));
   * store.dispatch(actions.reservations.carClassState.set('bundle_code', {bundle_code:'GOLD'}));
   * store.dispatch(actions.reservations.carClassState.set('prepay_selected', {prepay_selected:true}));
   * store.dispatch(actions.reservations.carClassState.set('redemption_day_count', {redemption_day_count: 1}));
   */
  set(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'code') {
          dispatch(setCarClassCode(args.code));
        } else if (type === 'bundle_code') {
          dispatch(setBundleCode(args.bundle_code));
        } else if (type === 'prepay_selected') {
          dispatch(setPrePay(args.prepay_selected));
        } else if(type === 'redemption_day_count') {
          dispatch(setRedemptionDayCount(args.redemption_day_count));
        }
      } else {
        dispatch(setResCarClass(args));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for reservation.selectCarClass payload data
   * @returns {Function}
   * @memberof actions.reservations.carClassState
   * @example
   * store.dispatch(actions.reservations.carClassState.unset());
   */
  unset() {
    return dispatch => {
      dispatch(clearResCarClass());
    };
  }
};

/**
 * @name selectCarClass
 * @desc Make the GMA reservations/selectCarClass call with args or state data
 * @param {Object} [car_class] {
 *   code: 'ECAR',
 *   name: 'Economy'
 * }
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.selectCarClass(car_class));
 */
export function selectCarClass(car_class, data = {}, requestOpts = {}) {
  return (dispatch, getState) => {
    if (!car_class) car_class = getState().getIn(CAR_CLASS).toJS();
    const {code, name, bundle_code, prepay_selected, redemption_day_count} = car_class;
    const payload = Object.assign(
      {car_class_code: code},
      (bundle_code ? {bundle_code} : null),
      (prepay_selected ? {prepay_selected} : null),
      (redemption_day_count ? {redemption_day_count} : null)
    );
    Object.assign(data, {payload});
    return dispatch(request({
      service: reservations.selectCarClass,
      data,
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) dispatch(setResCarClass({code, name, bundle_code, prepay_selected, redemption_day_count}));
      return resp;
    });
  };
}

/**
 * @name upgradeCarClass
 * @desc Make the GMA reservations/upgradeSelect call with car_class_code; Update state
 * @param {String} car_class_code
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.upgradeCarClass(car_class_code));
 */
export function upgradeCarClass(car_class_code, data = {}, requestOpts = {}) {
  return dispatch => {
    const payload = {car_class_code};
    Object.assign(data, {payload});
    return dispatch(request({
      service: reservations.upgradeSelect,
      data,
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        const {code, name, bundle_code, prepay_selected, redemption_day_count} = get(resp, 'gbo.reservation.car_class_details');
        dispatch(setResCarClass(Object.assign(
          {code, name},
          (bundle_code ? {bundle_code} : null),
          (prepay_selected ? {prepay_selected} : null),
          (redemption_day_count ? {redemption_day_count} : null)
        )));
      }
      return resp;
    });
  };
}
